import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import SendFeedback from './SendFeedback.jsx'
import UserProfile from './UserProfile.jsx'
// import NotificationSettings from '../pages/NotificationSettings.jsx'
import HelpSupport from '../pages/HelpSupport.jsx'

/**
 * Custom hook for handling click outside to close functionality
 * Same pattern as DeviceSelector.jsx
 */
const useOutsideClick = (ref, isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose()
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [ref, isOpen, onClose])
}

/**
 * Memoized user avatar component for performance
 */
const UserAvatar = memo(({ user, displayName, className, style }) => {
  const userInitials = useMemo(() => 
    displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
    [displayName],
  )

  return user?.photoURL ? (
    <img 
      src={user.photoURL} 
      alt="User Avatar"
      className={className}
      style={style}
      loading="lazy"
      decoding="async"
    />
  ) : (
    <div 
      className={className} 
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        lineHeight: '1',
      }}
    >
      {userInitials}
    </div>
  )
})

UserAvatar.displayName = 'UserAvatar'

/**
 * Memoized menu item component
 */
const MenuItem = memo(({ icon, label, onClick, className = '' }) => (
  <button 
    className={`dropdown-item user-menu-item-world-class ${className}`}
    onClick={onClick}
    type="button"
    style={{
      transition: 'all 0.2s ease',
      borderRadius: '8px',
      margin: '0.1875rem',
      padding: '0.8125rem 0.9375rem',
      border: '1px solid transparent',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
      width: 'calc(100% - 1rem)', // Much more conservative width to prevent overflow
      maxWidth: 'calc(100% - 1rem)',
      overflow: 'hidden', // Prevent any content overflow
    }}
  >
    <i className={`bi ${icon} user-menu-icon-world-class`} style={{
      width: '16px',
      height: '16px',
      marginRight: '0.75rem',
      flexShrink: '0',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}></i>
    <span>{label}</span>
  </button>
))

MenuItem.displayName = 'MenuItem'

/**
 * World-Class User Menu component optimized for React 19+
 * Features:
 * - Premium user profile with smooth interactions
 * - Smart account settings access
 * - Professional help & support integration
 * - Secure logout functionality
 * - Performance optimized with memo and proper state management
 * - Accessibility compliant (WCAG 2.1 AA)
 */
const UserMenu = memo(() => {
  const [isOpen, setIsOpen] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showAccount, setShowAccount] = useState(false)
  // const [showNotifications, setShowNotifications] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const dropdownRef = useRef(null)
  const { user, logout } = useCloudContext()
  const navigate = useNavigate()

  // Memoized user data
  const displayName = useMemo(() => 
    user?.displayName || user?.email || 'User',
    [user?.displayName, user?.email],
  )

  // Handlers using DeviceSelector pattern
  const toggleDropdown = useCallback(() => setIsOpen(prev => !prev), [])
  const closeDropdown = useCallback(() => setIsOpen(false), [])
  
  // Use custom hook for outside clicks
  useOutsideClick(dropdownRef, isOpen, closeDropdown)

  // Memoized action handlers
  const handleActions = useMemo(() => ({
    async logout() {
      try {
        await logout()
      } catch (error) {
        console.error('Logout error:', error)
      }
    },
    navigate: (path) => navigate(path),
    showFeedback: () => setShowFeedback(true),
    showAccount: () => setShowAccount(true),
    // showNotifications: () => setShowNotifications(true),
    showHelp: () => setShowHelp(true),
  }), [logout, navigate])

  // Optimized menu item click handler
  const createMenuHandler = useCallback((action, param) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false) // Close dropdown first
    
    switch (action) {
      case 'navigate':
        handleActions.navigate(param)
        break
      case 'feedback':
        handleActions.showFeedback()
        break
      case 'account':
        handleActions.showAccount()
        break
      // case 'notifications':
      //   handleActions.showNotifications()
      //   break
      case 'help':
        handleActions.showHelp()
        break
      case 'logout':
        handleActions.logout()
        break
    }
  }, [handleActions])

  const closeFeedback = useCallback(() => setShowFeedback(false), [])
  const closeAccount = useCallback(() => setShowAccount(false), [])
  // const closeNotifications = useCallback(() => setShowNotifications(false), [])
  const closeHelp = useCallback(() => setShowHelp(false), [])

  return (
    <div className="dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      <style>
        {`
          .user-menu-world-class {
            display: flex;
            align-items: center;
            gap: 0.625rem;
            background: var(--bg-header-gradient);
            border: 1px solid var(--border-color-dark);
            border-radius: 6px;
            padding: 0.5rem 0.75rem;
            transition: all 0.2s ease;
            cursor: pointer;
            min-width: 160px;
            font-size: 0.875rem;
            user-select: none;
            box-shadow: 0 1px 3px var(--shadow-color-light);
            animation: userMenuFadeIn 0.4s ease-out;
          }
          
          .user-menu-world-class:hover {
            border-color: var(--bs-primary);
            box-shadow: 0 2px 8px var(--color-hover-medium);
            transform: translateY(-1px);
            background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
          }
          
          .user-menu-world-class:focus {
            outline: 2px solid var(--color-focus-ring);
            outline-offset: 2px;
            box-shadow: 0 2px 12px rgba(var(--bs-primary-rgb), 0.2);
            border-color: var(--bs-primary);
          }
          
          .user-avatar-world-class {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75rem;
            font-weight: 600;
            color: white;
            background: #6c757d;
            border: 2px solid rgba(255, 255, 255, 0.9);
            flex-shrink: 0;
            animation: userAvatarScale 0.3s ease-out;
            text-align: center;
            line-height: 1;
          }
          
          .user-avatar-world-class img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            object-fit: cover;
          }
          
          .user-info-world-class {
            flex: 1;
            min-width: 0;
            text-align: left;
            animation: userInfoSlideIn 0.3s ease-out 0.1s both;
          }
          
          .user-name-world-class {
            font-weight: 600;
            color: var(--text-primary);
            line-height: 1.3;
            font-size: 0.875rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            animation: userNameSlideIn 0.3s ease-out 0.1s both;
          }
          
          .user-email-world-class {
            font-size: 0.7rem;
            color: var(--text-muted);
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            animation: userStatusSlideIn 0.3s ease-out 0.15s both;
          }
          
          .chevron-user-world-class {
            transition: transform 0.2s ease;
            color: var(--text-muted);
            flex-shrink: 0;
          }
          
          .user-menu-world-class:hover .chevron-user-world-class {
            color: var(--bs-primary);
          }
          
          .user-menu-world-class[aria-expanded="true"] .chevron-user-world-class {
            transform: rotate(180deg);
            color: var(--bs-primary);
          }
          
          .user-menu-item-world-class:hover {
            background: rgba(var(--bs-primary-rgb), 0.08) !important;
            border-color: rgba(var(--bs-primary-rgb), 0.2) !important;
            color: inherit !important;
            max-width: calc(100% - 1rem) !important;
            overflow: hidden !important;
          }
          
          @keyframes userMenuFadeIn {
            from {
              opacity: 0;
              transform: translateY(-4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes userAvatarScale {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes userInfoSlideIn {
            from {
              opacity: 0;
              transform: translateX(-8px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes userNameSlideIn {
            from {
              opacity: 0;
              transform: translateX(-8px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes userStatusSlideIn {
            from {
              opacity: 0;
              transform: translateX(-8px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes userDropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-8px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <button
        className="user-menu-world-class"
        type="button"
        onClick={toggleDropdown}
        title="User Menu"
        aria-expanded={isOpen}
        aria-label={`User menu for ${displayName}`}
        key={user?.uid || 'anonymous'}
      >
        <UserAvatar 
          user={user} 
          displayName={displayName} 
          className="user-avatar-world-class" 
          key={`avatar-${user?.uid || 'anonymous'}`}
        />
        
        <div className="user-info-world-class d-none d-md-block" key={`info-${user?.uid || 'anonymous'}`}>
          <div className="user-name-world-class">{displayName}</div>
          <div className="user-email-world-class">{user?.email}</div>
        </div>
        
        <i className="bi bi-chevron-down chevron-user-world-class"></i>
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show" style={{ 
          minWidth: '280px', 
          position: 'absolute',
          right: '0',
          top: '100%',
          marginTop: '0.25rem',
          zIndex: 2000,
          border: '1px solid var(--border-color-light)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px var(--shadow-color-medium)',
          backdropFilter: 'blur(8px)',
          background: 'rgba(255, 255, 255, 0.95)',
          animation: 'userDropdownSlideIn 0.2s ease-out',
          overflow: 'hidden',
        }}>
          <div className="dropdown-header" style={{
            padding: '1rem',
            background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-quaternary) 100%)',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <UserAvatar 
              user={user} 
              displayName={displayName} 
              className="user-avatar-dropdown-world-class" 
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                background: '#6c757d',
                border: '3px solid rgba(255, 255, 255, 0.9)',
                flexShrink: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                lineHeight: '1',
              }}
            />
            <div style={{ flex: '1', minWidth: '0' }}>
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1rem', marginBottom: '0.25rem' }}>
                {displayName}
              </div>
              <small style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                {user?.email}
              </small>
            </div>
          </div>
          
          <div className="dropdown-divider"></div>
          
          <MenuItem 
            icon="bi-person-circle"
            label="Account"
            onClick={createMenuHandler('account')}
          />
          
          {/* TODO: Implement Notification Settings later
          <MenuItem 
            icon="bi-bell"
            label="Notification Settings"
            onClick={createMenuHandler('notifications')}
          />
          */}
          
          <div className="dropdown-divider"></div>
          
          <MenuItem 
            icon="bi-question-circle"
            label="Help & Support"
            onClick={createMenuHandler('help')}
          />
          
          <MenuItem 
            icon="bi-chat-square-text"
            label="Send Feedback"
            onClick={createMenuHandler('feedback')}
          />
          
          <div className="dropdown-divider"></div>
          
          <MenuItem 
            icon="bi-box-arrow-right"
            label="Sign Out"
            onClick={createMenuHandler('logout')}
            className="user-menu-logout"
          />
        </div>
      )}

      <SendFeedback 
        isOpen={showFeedback} 
        onClose={closeFeedback} 
      />

      <UserProfile
        isOpen={showAccount}
        onClose={closeAccount}
      />

      {/* TODO: Implement Notification Settings later
      <NotificationSettings
        isOpen={showNotifications}
        onClose={closeNotifications}
      />
      */}

      <HelpSupport
        isOpen={showHelp}
        onClose={closeHelp}
      />
    </div>
  )
})

UserMenu.displayName = 'UserMenu'

export default UserMenu
